import { RbacRole, RbacRoleBinding, RbacSubject } from '@cast/types';

/**
 * Role names used for validation
 */
export const RBAC_ROLE_NAMES = {
  OWNER: 'Owner',
  ENTERPRISE_VIEWER: 'Enterprise Viewer',
  ENTERPRISE_OWNER: 'Enterprise Owner',
  VIEWER: 'Viewer',
  MEMBER: 'Member',
  ANALYST: 'Analyst',
} as const;

/**
 * Checks if a role is an Owner role
 */
export const isOwnerRole = (role: RbacRole | undefined): boolean => {
  return role?.name === RBAC_ROLE_NAMES.OWNER;
};

/**
 * Checks if a role is an Enterprise Viewer role
 */
export const isEnterpriseViewerRole = (role: RbacRole | undefined): boolean => {
  return role?.name === RBAC_ROLE_NAMES.ENTERPRISE_VIEWER;
};

/**
 * Checks if a role is an Enterprise Owner role
 */
export const isEnterpriseOwnerRole = (role: RbacRole | undefined): boolean => {
  return role?.name === RBAC_ROLE_NAMES.ENTERPRISE_OWNER;
};

/**
 * Checks if a subject is a user (not a service account or group)
 */
export const isUserSubject = (subject: RbacSubject): boolean => {
  return !!subject.user?.id;
};

/**
 * Checks if a subject matches a user ID
 */
export const subjectMatchesUserId = (
  subject: RbacSubject,
  userId: string
): boolean => {
  return subject.user?.id === userId;
};

/**
 * Gets all user IDs with a specific role in an organization
 */
export const getUsersWithRole = (
  roleBindings: RbacRoleBinding[],
  roleId: string,
  organizationScopeId?: string
): string[] => {
  const userIds = new Set<string>();

  roleBindings.forEach((binding) => {
    // Check if this binding has the target role
    if (binding.definition.roleId !== roleId) return;

    // If organizationScopeId is provided, check if the binding applies to that org
    if (organizationScopeId) {
      const hasOrgScope = binding.definition.scopes?.some(
        (scope) => scope.organization?.id === organizationScopeId
      );
      if (!hasOrgScope) return;
    }

    // Add all users from this binding
    binding.definition.subjects?.forEach((subject) => {
      if (isUserSubject(subject) && subject.user?.id) {
        userIds.add(subject.user.id);
      }
    });
  });

  return Array.from(userIds);
};

/**
 * Checks if removing/changing a role binding would leave the organization without any Owner users
 */
export const wouldRemoveLastOwner = (options: {
  roleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  targetUserId: string;
  organizationScopeId?: string;
}): boolean => {
  const { roleBindings, roles, targetUserId, organizationScopeId } = options;
  // Find the Owner role
  const ownerRole = roles.find((r) => isOwnerRole(r));
  if (!ownerRole?.id) return false;

  // Get all users with Owner role
  const ownerUserIds = getUsersWithRole(
    roleBindings,
    ownerRole.id,
    organizationScopeId
  );

  // Check if the target user is the only owner
  return ownerUserIds.length === 1 && ownerUserIds.includes(targetUserId);
};

/**
 * Checks if removing/changing a role binding would leave the organization without any Enterprise Owner users
 */
export const wouldRemoveLastEnterpriseOwner = (options: {
  roleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  targetUserId: string;
  organizationScopeId?: string;
}): boolean => {
  const { roleBindings, roles, targetUserId, organizationScopeId } = options;
  // Find the Enterprise Owner role
  const enterpriseOwnerRole = roles.find((r) => isEnterpriseOwnerRole(r));
  if (!enterpriseOwnerRole?.id) return false;

  // Get all users with Enterprise Owner role
  const enterpriseOwnerUserIds = getUsersWithRole(
    roleBindings,
    enterpriseOwnerRole.id,
    organizationScopeId
  );

  // Check if the target user is the only enterprise owner
  return (
    enterpriseOwnerUserIds.length === 1 &&
    enterpriseOwnerUserIds.includes(targetUserId)
  );
};

/**
 * Checks if a user is changing their own role from Owner to something else
 */
export const isSelfRoleDowngradeFromOwner = (options: {
  currentUserId: string;
  subject: RbacSubject;
  currentRoleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  newRoleId: string;
}): boolean => {
  const { currentUserId, subject, currentRoleBindings, roles, newRoleId } =
    options;
  // Check if the subject is the current user
  if (!subjectMatchesUserId(subject, currentUserId)) return false;

  // Find the Owner role
  const ownerRole = roles.find((r) => isOwnerRole(r));
  if (!ownerRole) return false;

  // Check if the new role is not Owner
  if (newRoleId === ownerRole.id) return false;

  // Check if user currently has Owner role
  const userOwnerBindings = currentRoleBindings.filter((binding) =>
    binding.definition.subjects?.some((s) =>
      subjectMatchesUserId(s, currentUserId)
    )
  );

  const hasOwnerRole = userOwnerBindings.some(
    (binding) => binding.definition.roleId === ownerRole.id
  );

  return hasOwnerRole;
};

/**
 * Checks if a user is changing their own role from Enterprise Owner to something else
 */
export const isSelfRoleDowngradeFromEnterpriseOwner = (options: {
  currentUserId: string;
  subject: RbacSubject;
  currentRoleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  newRoleId: string;
}): boolean => {
  const { currentUserId, subject, currentRoleBindings, roles, newRoleId } =
    options;
  // Check if the subject is the current user
  if (!subjectMatchesUserId(subject, currentUserId)) return false;

  // Find the Enterprise Owner role
  const enterpriseOwnerRole = roles.find((r) => isEnterpriseOwnerRole(r));
  if (!enterpriseOwnerRole) return false;

  // Check if the new role is not Enterprise Owner
  if (newRoleId === enterpriseOwnerRole.id) return false;

  // Check if user currently has Enterprise Owner role
  const userEnterpriseOwnerBindings = currentRoleBindings.filter((binding) =>
    binding.definition.subjects?.some((s) =>
      subjectMatchesUserId(s, currentUserId)
    )
  );

  const hasEnterpriseOwnerRole = userEnterpriseOwnerBindings.some(
    (binding) => binding.definition.roleId === enterpriseOwnerRole.id
  );

  return hasEnterpriseOwnerRole;
};

/**
 * Validation result type
 */
type RbacValidationResult = {
  isValid: boolean;
  errorMessage?: string;
};

/**
 * Validates if a user can be removed from an organization
 */
export const validateUserRemoval = (options: {
  userId: string;
  currentUserId: string;
  roleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  isChildOrganization: boolean;
  organizationScopeId?: string;
}): RbacValidationResult => {
  const {
    userId,
    currentUserId,
    roleBindings,
    roles,
    isChildOrganization,
    organizationScopeId,
  } = options;
  const isSelf = userId === currentUserId;

  // Check if this would remove the last owner
  if (
    wouldRemoveLastOwner({
      roleBindings,
      roles,
      targetUserId: userId,
      organizationScopeId,
    })
  ) {
    return {
      isValid: false,
      errorMessage: isSelf
        ? 'You cannot leave the organization as you are the last user with the Owner role. Please assign the Owner role to another user first.'
        : 'This user cannot be removed as they are the last user with the Owner role. Please assign the Owner role to another user first.',
    };
  }

  // Check if this would remove the last enterprise owner
  if (
    wouldRemoveLastEnterpriseOwner({
      roleBindings,
      roles,
      targetUserId: userId,
      organizationScopeId,
    })
  ) {
    return {
      isValid: false,
      errorMessage: isSelf
        ? 'You cannot leave the organization as you are the last user with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.'
        : 'This user cannot be removed as they are the last user with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.',
    };
  }

  // Check for Enterprise Viewer/Owner in child organizations
  if (isChildOrganization) {
    const enterpriseViewerRole = roles.find((r) => isEnterpriseViewerRole(r));
    const enterpriseOwnerRole = roles.find((r) => isEnterpriseOwnerRole(r));

    const hasEnterpriseRole = roleBindings.some((binding) => {
      const isEnterpriseRole =
        binding.definition.roleId === enterpriseViewerRole?.id ||
        binding.definition.roleId === enterpriseOwnerRole?.id;

      const hasUser = binding.definition.subjects?.some((s) =>
        subjectMatchesUserId(s, userId)
      );

      return isEnterpriseRole && hasUser;
    });

    if (hasEnterpriseRole) {
      return {
        isValid: false,
        errorMessage: isSelf
          ? 'You cannot leave this child organization as you have an Enterprise Viewer or Enterprise Owner role.'
          : 'Users with Enterprise Viewer or Enterprise Owner roles cannot be removed from child organizations.',
      };
    }
  }

  return { isValid: true };
};

/**
 * Validates if a role change is allowed
 */
export const validateRoleChange = (options: {
  currentUserId: string;
  subject: RbacSubject;
  currentRoleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  newRoleId: string;
  organizationScopeId?: string;
}): RbacValidationResult => {
  const {
    currentUserId,
    subject,
    currentRoleBindings,
    roles,
    newRoleId,
    organizationScopeId,
  } = options;

  // Check if user is downgrading their own Owner role
  if (
    isSelfRoleDowngradeFromOwner({
      currentUserId,
      subject,
      currentRoleBindings,
      roles,
      newRoleId,
    })
  ) {
    return {
      isValid: false,
      errorMessage:
        'You cannot downgrade your own Owner role. This would lock you out of administrative access.',
    };
  }

  // Check if user is downgrading their own Enterprise Owner role
  if (
    isSelfRoleDowngradeFromEnterpriseOwner({
      currentUserId,
      subject,
      currentRoleBindings,
      roles,
      newRoleId,
    })
  ) {
    return {
      isValid: false,
      errorMessage:
        'You cannot downgrade your own Enterprise Owner role. This would lock you out of administrative access.',
    };
  }

  // Check if this would remove the last owner (when changing from Owner to another role)
  const ownerRole = roles.find((r) => isOwnerRole(r));
  const targetUserId = subject.user?.id;

  if (
    ownerRole &&
    targetUserId &&
    newRoleId !== ownerRole.id &&
    wouldRemoveLastOwner({
      roleBindings: currentRoleBindings,
      roles,
      targetUserId,
      organizationScopeId,
    })
  ) {
    const isSelf = targetUserId === currentUserId;
    return {
      isValid: false,
      errorMessage: isSelf
        ? 'You cannot change your role as you are the last user with the Owner role. Please assign the Owner role to another user first.'
        : 'This role cannot be changed as this user is the last one with the Owner role. Please assign the Owner role to another user first.',
    };
  }

  // Check if this would remove the last enterprise owner (when changing from Enterprise Owner to another role)
  const enterpriseOwnerRole = roles.find((r) => isEnterpriseOwnerRole(r));

  if (
    enterpriseOwnerRole &&
    targetUserId &&
    newRoleId !== enterpriseOwnerRole.id &&
    wouldRemoveLastEnterpriseOwner({
      roleBindings: currentRoleBindings,
      roles,
      targetUserId,
      organizationScopeId,
    })
  ) {
    const isSelf = targetUserId === currentUserId;
    return {
      isValid: false,
      errorMessage: isSelf
        ? 'You cannot change your role as you are the last user with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.'
        : 'This role cannot be changed as this user is the last one with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.',
    };
  }

  return { isValid: true };
};

/**
 * Validates if a role binding can be deleted
 */
export const validateRoleBindingDeletion = (options: {
  roleBinding: RbacRoleBinding;
  currentUserId: string;
  allRoleBindings: RbacRoleBinding[];
  roles: RbacRole[];
  organizationScopeId?: string;
}): RbacValidationResult => {
  const {
    roleBinding,
    currentUserId,
    allRoleBindings,
    roles,
    organizationScopeId,
  } = options;
  const ownerRole = roles.find((r) => isOwnerRole(r));
  const enterpriseOwnerRole = roles.find((r) => isEnterpriseOwnerRole(r));

  // Check if this is an Owner role binding
  if (roleBinding.definition.roleId === ownerRole?.id) {
    // Check if deleting this binding would remove the last owner
    const usersInBinding = roleBinding.definition.subjects
      ?.filter(isUserSubject)
      .map((s) => s.user!.id);

    if (usersInBinding && usersInBinding.length > 0) {
      // For each user in this binding, check if they would be the last owner
      for (const userId of usersInBinding) {
        if (
          wouldRemoveLastOwner({
            roleBindings: allRoleBindings,
            roles,
            targetUserId: userId,
            organizationScopeId,
          })
        ) {
          const isSelf = userId === currentUserId;
          return {
            isValid: false,
            errorMessage: isSelf
              ? 'You cannot delete this role binding as you are the last user with the Owner role. Please assign the Owner role to another user first.'
              : 'This role binding cannot be deleted as it contains the last user with the Owner role. Please assign the Owner role to another user first.',
          };
        }
      }
    }
  }

  // Check if this is an Enterprise Owner role binding
  if (roleBinding.definition.roleId === enterpriseOwnerRole?.id) {
    // Check if deleting this binding would remove the last enterprise owner
    const usersInBinding = roleBinding.definition.subjects
      ?.filter(isUserSubject)
      .map((s) => s.user!.id);

    if (usersInBinding && usersInBinding.length > 0) {
      // For each user in this binding, check if they would be the last enterprise owner
      for (const userId of usersInBinding) {
        if (
          wouldRemoveLastEnterpriseOwner({
            roleBindings: allRoleBindings,
            roles,
            targetUserId: userId,
            organizationScopeId,
          })
        ) {
          const isSelf = userId === currentUserId;
          return {
            isValid: false,
            errorMessage: isSelf
              ? 'You cannot delete this role binding as you are the last user with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.'
              : 'This role binding cannot be deleted as it contains the last user with the Enterprise Owner role. Please assign the Enterprise Owner role to another user first.',
          };
        }
      }
    }
  }

  return { isValid: true };
};
